import { dayOffsetToDate, applyDelta } from '../shared'
import iconTeamCorn from './team-icons/corn.png'
import iconTeamTomato from './team-icons/tomato.png'
import { demoUsersByUuid, demoUser } from '../tenants/tenant'
import { demoProject } from './project'

export const demoProjectMember = {
  uuid: demoUser.uuid,
  userUuid: demoUser.uuid,
  name: demoUser.name,
  iconUrl: demoUser.iconUrl,
}

export const buildDemoProjectMember = (
  userUuid: string,
  memberUuid: string
) => ({
  uuid: memberUuid,
  lockVersion: 1,
  revision: 'demo-revision',
  children: [] as any[],
  projectUuid: demoProject.uuid,
  user: demoUsersByUuid[userUuid],
  team: undefined,
  projectAssignedDate: dayOffsetToDate(0), // day 0 = 2026-04-01
  projectReleasedDate: '',
})

// ProjectMemberOptions.createDeleteRequestByRow が member uuid ではなく
// userUuid しか送ってこない（delta/batch ハンドラ参照）ため userUuid で
// キーイングする。demoUsersByUuid の全ユーザーを最初から参加済みメンバー
// として持たせることで、WBSグリッドの担当者ピッカーや ProjectMember 画面に
// demoUser 以外の選択肢も表示されるようにしている。
export const demoProjectMembersByUserUuid: { [userUuid: string]: any } = {
  [demoUser.uuid]: buildDemoProjectMember(
    demoUser.uuid,
    demoProjectMember.uuid
  ),
  'demo-user-2': buildDemoProjectMember('demo-user-2', 'demo-member-2'),
  'demo-user-3': buildDemoProjectMember('demo-user-3', 'demo-member-3'),
  'demo-user-4': buildDemoProjectMember('demo-user-4', 'demo-member-4'),
  'demo-user-5': buildDemoProjectMember('demo-user-5', 'demo-member-5'),
}

// WbsItemDeltaInput は accountable/responsible/assignee を
// *Uuid（ProjectMemberSearchOption.uuid と同じ project member uuid）の
// deltaとして送ってくる。一方グリッドが実際に読むのは
// accountable/responsible/assignee のオブジェクトフィールドなので、
// plans/delta/batch ハンドラの `edited` ループで変換する際に対応表として使う。
export const WBS_MEMBER_REF_FIELDS: { [deltaField: string]: string } = {
  accountableUuid: 'accountable',
  responsibleUuid: 'responsible',
  assigneeUuid: 'assignee',
}

// シングルシートのチーム選択（GET teams/list）と teamUuid delta 適用時の
// 名前解決に使う。WbsItemEntity のチームマッピング
// （uuid/displayName/iconUrl を読む）が保存後に参照できるようにする。
//
// iconUrlは実行時にCDNを引かず、静的importしたPNGをそのまま渡す
// （user-icons/と同じ方針）。社外公開デモを本番CDNに依存させないため。
export const demoTeams = [
  {
    uuid: 'demo-team-a-uuid',
    projectUuid: demoProject.uuid,
    code: 'TEAM_A',
    displayName: 'Aチーム',
    iconUrl: iconTeamCorn,
  },
  {
    uuid: 'demo-team-b-uuid',
    projectUuid: demoProject.uuid,
    code: 'TEAM_B',
    displayName: 'Bチーム',
    iconUrl: iconTeamTomato,
  },
]
export const demoTeamsByUuid: { [uuid: string]: any } = Object.fromEntries(
  demoTeams.map(team => [team.uuid, team])
)

// WBS項目の accountable/responsible/assignee はグリッドが
// { uuid, userUuid, name, iconUrl } の形で読むため、初期データ生成
// （wbs-items.ts）から user uuid だけで参照できるようにする。
// メンバーは実行時に削除されうる（ProjectMember画面）。存在しない場合は
// 担当者未設定を表すnullを返す。
export const demoProjectMemberRefByUserUuid = (userUuid: string) => {
  const member = demoProjectMembersByUserUuid[userUuid]
  if (!member?.user) return null
  return {
    uuid: member.uuid,
    userUuid: member.user.uuid,
    name: member.user.name,
    iconUrl: member.user.iconUrl,
  }
}

// 初期データ（wbs-items.ts）が参照するのは常に存在するメンバーなので、
// 未設定(null)を混入させずそのまま担当者フィールドへ渡せる形で返す。
export const requireDemoProjectMemberRef = (userUuid: string) => {
  const ref = demoProjectMemberRefByUserUuid(userUuid)
  if (!ref) {
    throw new Error(`demo project member not found: ${userUuid}`)
  }
  return ref
}

// デモの体制:
//   Aチーム: 塩崎 一郎（責任者・PM）／山田 太郎
//   Bチーム: 斎藤 健一（責任者）／吉田 花子／伊藤 大輔
// 各チームに責任者1名を置き、責任者は自チームの成果物のレビュー責任を持つ。
// 責任者自身も担当者として成果物を持つ（プレイングマネージャー）。
const DEMO_TEAM_ROSTER_SEEDS = [
  {
    teamUuid: demoTeams[0].uuid,
    accountableUserUuid: demoUser.uuid, // 塩崎 一郎
    assigneeUserUuids: ['demo-user-2', demoUser.uuid], // 山田 太郎, 塩崎 一郎
  },
  {
    teamUuid: demoTeams[1].uuid,
    accountableUserUuid: 'demo-user-4', // 斎藤 健一
    assigneeUserUuids: ['demo-user-3', 'demo-user-5'], // 吉田 花子, 伊藤 大輔
  },
] as const

// プロジェクトマネージャー。ワークグループ・工程といったルート行の
// 責任者・担当者を兼ねる。
export const DEMO_PM_USER_UUID = demoUser.uuid

// 責任者（accountable）はチームごとに1名。
export const DEMO_ACCOUNTABLE_USER_UUIDS = DEMO_TEAM_ROSTER_SEEDS.map(
  seed => seed.accountableUserUuid
)

// メンバーはどちらか一方のチームに所属する。WBS項目のチームは担当者の
// 所属から決まるため（wbs-items.tsのresolveItemMembers）、所属の定義は
// ここだけに置く。
const DEMO_TEAM_UUID_BY_USER_UUID: { [userUuid: string]: string } =
  Object.fromEntries(
    DEMO_TEAM_ROSTER_SEEDS.flatMap(seed =>
      [seed.accountableUserUuid, ...seed.assigneeUserUuids].map(userUuid => [
        userUuid,
        seed.teamUuid,
      ])
    )
  )

export const demoTeamByUserUuid = (userUuid: string) =>
  demoTeamsByUuid[DEMO_TEAM_UUID_BY_USER_UUID[userUuid]] ?? demoTeams[0]

// 成果物の担当者は各成果物が直接持つ（wbs-items.tsのassigneeUserUuid）ため、
// ここは巡回順ではなくチーム編成の定義。責任者の解決と、担当者の所属から
// チームを引く（DEMO_TEAM_UUID_BY_USER_UUID）ために公開する。
export const DEMO_TEAM_ROSTERS = DEMO_TEAM_ROSTER_SEEDS.map(seed => ({
  team: demoTeamsByUuid[seed.teamUuid],
  accountableUserUuid: seed.accountableUserUuid,
  assigneeUserUuids: [...seed.assigneeUserUuids],
}))

// ProjectMember画面のチーム列に所属を表示するため、生成済みのメンバーへ
// チームを設定する。
Object.entries(demoProjectMembersByUserUuid).forEach(([userUuid, member]) => {
  member.team = demoTeamByUserUuid(userUuid)
})

// WBS_MEMBER_REF_FIELDS の delta（member uuidを持つ）からグリッド表示用の
// オブジェクトへ解決できるよう、member uuidをキーに demoProjectMember と
// 同じ形（uuid, userUuid, name, iconUrl）で持たせる。
export const getDemoProjectMemberRefsByUuid = () => {
  const refsByUuid: { [uuid: string]: any } = {}
  Object.values(demoProjectMembersByUserUuid).forEach((member: any) => {
    const ref = demoProjectMemberRefByUserUuid(member.user?.uuid)
    if (ref) refsByUuid[member.uuid] = ref
  })
  return refsByUuid
}

// accountable/responsible/assignee/teamのdelta（*Uuid）はグリッドが読む
// member/teamオブジェクトのフィールドへ変換する必要があるため、それ以外の
// フィールドのapplyDeltaと分岐処理を共通化する。plans/delta/batch・
// ticket_lists/delta/batch・tickets/delta/batchの各editedループが同一ロジック
// を必要とするため、ここに1本化して重複を避ける。
export const applyWbsItemFieldDelta = (
  currentBody: Record<string, any>,
  field: string,
  delta: { oldValue?: unknown; newValue?: unknown } | undefined
): void => {
  const memberObjectField = WBS_MEMBER_REF_FIELDS[field]
  if (memberObjectField) {
    const demoProjectMemberRefsByUuid = getDemoProjectMemberRefsByUuid()
    const newMemberUuid = (delta as { newValue?: string })?.newValue
    currentBody[memberObjectField] = newMemberUuid
      ? (demoProjectMemberRefsByUuid[newMemberUuid] ?? null)
      : null
    return
  }
  if (field === 'teamUuid') {
    const newTeamUuid = (delta as { newValue?: string })?.newValue
    currentBody.team = newTeamUuid
      ? (demoTeamsByUuid[newTeamUuid] ?? null)
      : null
    return
  }
  // グリッドが読むのはsprint/sprintsオブジェクトで、sprintUuid（生のuuid）を
  // そのまま書き込んでも表示に反映されず、不要なフィールドが残るだけ。
  // 割り当ての実体はsprint_product_items/batch・sprint_backlogs/batchが
  // 持つため、ここでは無視する（sprintの解決はそれらのハンドラ側で行う）。
  if (field === 'sprintUuid') return
  currentBody[field] = applyDelta(currentBody[field], delta)
}

// editedItem.wbsItemはuuid/type（生値）とフィールドdelta（{oldValue,newValue}）
// が混在して送られてくる（toDeltaInput参照）ため、uuid/typeを除外してから
// 各フィールドにapplyWbsItemFieldDeltaを適用する処理をここに1本化する。
// plans/delta/batch・ticket_lists/delta/batch・tickets/delta/batch・
// tickets/deltaの4箇所が同一ロジックを必要とする。
// watchers/additionalPropertyValuesも同様にdeltaではなく生値（配列）で
// 送られてくることがある（createDeltaRequestByRow, lib/functions/wbsItem.ts）。
// applyDeltaは配列をtruthyなdeltaオブジェクトと誤認し.newValueを読んで
// undefinedを返してしまうため、uuid/typeと同様に除外する。
// 実績日連動の判定に使うステータス。wbs-items.tsのDemoWbsItemStatusと同値だが、
// wbs-items.ts -> members.tsの依存方向を保つためここで定義する。
type DemoStatus = 'TODO' | 'DOING' | 'REVIEW' | 'DONE' | 'DISCARD'

// ステータス連動で設定するサブステータス。wbs-items.tsの
// DEFAULT_SUBSTATUS_BY_STATUSと同じ対応（依存方向の都合で再掲）。
const SUBSTATUS_BY_STATUS: { [status in DemoStatus]: string } = {
  TODO: 'NOT_STARTED',
  DOING: 'IN_PROGRESS',
  REVIEW: 'CODE_REVIEW',
  DONE: 'COMPLETED',
  DISCARD: 'DISCARDED',
}

// ステータスの進行順。実績日連動でこの順序を逆行させないために使う。
// DISCARDは進行ラインから外れた終端状態なので含めず、別途ガードする。
const STATUS_PROGRESS_ORDER: DemoStatus[] = ['TODO', 'DOING', 'REVIEW', 'DONE']

// 実績日の登録に応じて前進させたステータスを返す。変更不要ならundefined。
// 実績開始日 -> DOING、実績終了日 -> DONE（終了日が優先）。
// 実績日を消した場合と、現在値より後退する場合は変更しない。
// 本番はバックエンドが保存時に行う処理なので、モックでも更新系ハンドラから
// 呼んで同じ見え方にする。
export const resolveStatusByActualDate = (
  currentStatus: DemoStatus | undefined,
  actualDate: { startDate?: string | null; endDate?: string | null } | undefined
): DemoStatus | undefined => {
  const nextStatus = actualDate?.endDate
    ? 'DONE'
    : actualDate?.startDate
      ? 'DOING'
      : undefined
  if (!nextStatus || nextStatus === currentStatus) return undefined
  // 破棄済みは実績日入力で復活させない
  if (currentStatus === 'DISCARD') return undefined
  // レビュー中のタスクに実績開始日を入れてもDOINGへ巻き戻さない
  const currentIndex = STATUS_PROGRESS_ORDER.indexOf(
    currentStatus as DemoStatus
  )
  if (
    currentIndex >= 0 &&
    STATUS_PROGRESS_ORDER.indexOf(nextStatus) <= currentIndex
  ) {
    return undefined
  }
  return nextStatus
}

const RAW_VALUE_FIELDS = new Set(['watchers', 'additionalPropertyValues'])

export const applyWbsItemDeltas = (
  currentBody: Record<string, any>,
  wbsItemInput: { uuid?: string; type?: string } & Record<string, unknown>
): void => {
  const { uuid: _uuid, type: _type, ...fields } = wbsItemInput
  for (const [field, value] of Object.entries(fields)) {
    if (RAW_VALUE_FIELDS.has(field)) {
      currentBody[field] = value
      continue
    }
    applyWbsItemFieldDelta(
      currentBody,
      field,
      value as { oldValue?: unknown; newValue?: unknown } | undefined
    )
  }
  // 実績日が更新された行はステータスも前進させる。クライアントは
  // ステータスのdeltaを送ってこないので、ここで導出しないと
  // 「実績終了日を入れたのに未着手のまま」になる。
  if ('actualDate' in fields) {
    const nextStatus = resolveStatusByActualDate(
      currentBody.status,
      currentBody.actualDate
    )
    if (nextStatus) {
      currentBody.status = nextStatus
      currentBody.substatus = SUBSTATUS_BY_STATUS[nextStatus]
    }
  }
}

// toCreateInput（WbsItem/model）はaccountable/responsible/assignee/teamUuidを
// delta ({oldValue, newValue}) ではなく生のuuid値（またはundefined）で送って
// くる。applyWbsItemFieldDeltaはdelta形しか読まないため、POST /tickets等の
// create系ハンドラでもmember/teamオブジェクトへ変換できるよう、生値を
// { newValue: value } の擬似deltaに包んでapplyWbsItemFieldDeltaへ委譲する。
export const applyWbsItemFieldValue = (
  currentBody: Record<string, any>,
  field: string,
  value: unknown
): void => {
  applyWbsItemFieldDelta(currentBody, field, { newValue: value })
}
